import React, { useState, useEffect } from "react";
import { useAuth } from "./contexts/AuthContext";
import { deleteUser } from "./utils/userManagementApi";
import logger from "./utils/logger";
import "./DeleteUserConfirmModal.css";
import "./AddUserModal.css"; // Reuse button styles

const DeleteUserConfirmModal = ({ user, onClose, onUserDeleted }) => {
  const { getAccessToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [typedEmail, setTypedEmail] = useState("");

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && !loading) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose, loading]);

  const handleConfirmDelete = async () => {
    // Prevent deletion of Full Admin users
    if (user.role === "Full Admin") {
      setError("Full Admin users cannot be deleted for security reasons.");
      return;
    }

    // Check typed email matches (case-insensitive)
    if (typedEmail.toLowerCase() !== user.email.toLowerCase()) {
      setError(
        "Email address does not match. Please type the user's email address to confirm."
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await deleteUser(user.userId, getAccessToken);
      logger.info("User deleted successfully", { userId: user.userId });
      onUserDeleted();
    } catch (err) {
      logger.error("Failed to delete user", err);
      setError(err.message || "Failed to delete user");
      setLoading(false);
    }
  };

  return (
    <div className="delete-user-confirm-modal-overlay">
      <div className="delete-user-confirm-modal">
        <h3>⚠️ Delete User</h3>

        {error && <div className="delete-user-message error">{error}</div>}

        {user.role === "Full Admin" && (
          <div
            className="delete-user-message error"
            style={{ marginBottom: "15px" }}
          >
            ⚠️ Full Admin users cannot be deleted for security reasons.
          </div>
        )}

        <div className="delete-user-info">
          <p>
            <strong>Name:</strong> {user.firstName} {user.lastName}
          </p>
          <p>
            <strong>Email:</strong> {user.email}
          </p>
          <p>
            <strong>Role:</strong> {user.role}
          </p>
          <p>
            <strong>Organizations:</strong> {user.organizationCount || 0}
          </p>
        </div>

        <div className="delete-warning">
          <strong>Warning: This action cannot be undone!</strong>
          Deleting this user will:
          <ul
            style={{ marginTop: "8px", marginBottom: "0", paddingLeft: "20px" }}
          >
            <li>Remove the user from Auth0</li>
            <li>Delete their profile from the database</li>
            {user.organizationCount > 0 && (
              <li>
                <strong>
                  Remove access to {user.organizationCount} organization(s)
                </strong>
              </li>
            )}
          </ul>
        </div>

        <p className="delete-user-confirm-text">
          Are you sure you want to delete this user?
        </p>

        <div className="delete-user-typed-confirmation">
          <label>Type the user's email address to confirm:</label>
          <input
            type="text"
            className="delete-user-typed-input"
            value={typedEmail}
            onChange={(e) => setTypedEmail(e.target.value)}
            placeholder={user.email}
            disabled={loading || user.role === "Full Admin"}
          />
        </div>

        <div className="delete-user-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="delete-confirm-btn"
            onClick={handleConfirmDelete}
            disabled={
              loading ||
              user.role === "Full Admin" ||
              typedEmail.toLowerCase() !== user.email.toLowerCase()
            }
          >
            {loading ? "Deleting..." : "Yes, Delete User"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteUserConfirmModal;
